import { FunctionComponent, ReactNode, SVGAttributes, useState } from 'react';

import { Box, Flex } from '@chakra-ui/react';
import { useToast } from 'components/hooks/useToast';
import { isEmpty } from 'lodash';
import { BulkUploadStatus } from 'screens/admin/Employees/BulkUploadSidePanel/components/BulkUploadStatus';
import { downloadBulkUploadOKRSampleFile } from 'screens/admin/Objectives/bulkUploadAccessControlApis';
import { checkIntentLandingPagesFeatureEnabled } from 'utils/helpers/featureFlags';

import { MeshGuide, Settings } from 'assets/assetsV1/iconComponents';
import CheckInErrorIllustration from 'assets/assetsV1/illustrations/deerIllustrations/checkInError.svg';
import CheckInSuccessIllustration from 'assets/assetsV1/illustrations/deerIllustrations/checkInSuccess.svg';
import CreateGoalIllustration from 'assets/assetsV1/illustrations/newIllustrations/createGoal.svg';

import { UPLOAD_GOAL_HELP_URL } from 'constants/ProjectConstants';

import { Button as ButtonToolkitVersion } from 'uiToolkitV1/Button';
import { EmptyScreen } from 'uiToolkitV1/EmptyScreen';
import { TextBodyMedium } from 'uiToolkitV1/Typography';

import {
  BulkUploadErrorUI,
  BulkUploadStatusType,
  BulkUploadSuccessUI,
  BulkUploadHeading as Heading,
  InvalidFileError,
  MAX_FILE_SIZE,
  UploadFileUI,
  UploadingProgressUI,
} from './BulkUploadComponent';

const BulkUploadScreen = ({
  handleFileAttached,
  handleFileUploadClicked,
  handleDownloadQuickGoalSampleFile,
  handleDownloadExtendedGoalSampleFile,
  handleViewGoalUploadGuide,
}: {
  handleFileAttached: () => void;
  handleFileUploadClicked: () => void;
  handleDownloadQuickGoalSampleFile: () => void;
  handleDownloadExtendedGoalSampleFile: () => void;
  handleViewGoalUploadGuide: () => void;
}) => {
  const [refetchUploadStatusToggle, setRefetchUploadStatusToggle] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<BulkUploadStatusType>({});

  const toast = useToast();

  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isInvalidFile, setInvalidFile] = useState<InvalidFileError>({ status: false }); // invalid file means  we are not able to upload the csv, either size is too large or column format is wrong
  const [uploadingCSVFailed, setUploadingCSVFailed] = useState<boolean>(false); // csv failed means  we are able to upload the csv but during goal creation some goals failed to create due to wrong data

  // this flag forcefully renders the starting UI to upload file ignoring the value of uploadStatus
  const [forceShowUploadFileUI, setForceShowUploadFileUI] = useState<boolean>(false);

  const validateUploadFile = (file: File) => {
    if (file.size > MAX_FILE_SIZE) {
      setInvalidFile({ status: true });
      return false;
    }
    return true;
  };

  const resetOldFileErrors = () => {
    setInvalidFile({ status: false });
    setUploadingCSVFailed(false);
  };

  const selectUploadFile = (file: File) => {
    resetOldFileErrors();
    validateUploadFile(file);
    setSelectedFile(file);
  };

  const getHeading = (): JSX.Element => {
    if (forceShowUploadFileUI) {
      return <Heading>Import your goal sheet</Heading>;
    }
    const { status, recordsUpdated, totalRecords } = uploadStatus || {};
    switch (status) {
      case 'IN_PROGRESS':
        return <Heading>Import your goal sheet</Heading>;
      case 'ERROR':
        return recordsUpdated > 0 ? (
          <Box display="flex" flexDir="column">
            <Heading subHeading={`${recordsUpdated} out of ${totalRecords} lines were uploaded successfully`}>
              File uploaded partially
            </Heading>
          </Box>
        ) : (
          <Heading>File upload failed.</Heading>
        );

      case 'VALIDATING':
        return <Heading>Import your goal sheet</Heading>;
      case 'FINISHED':
        return <Heading>File successfully uploaded.</Heading>;
      default:
        return <Heading>Import your goal sheet</Heading>;
    }
  };

  const getEmptyScreenImage = (): FunctionComponent<SVGAttributes<SVGElement>> => {
    if (forceShowUploadFileUI) {
      return CreateGoalIllustration;
    }
    switch (uploadStatus?.status) {
      case 'IN_PROGRESS':
        return CreateGoalIllustration;
      case 'ERROR':
        return CheckInErrorIllustration;
      case 'VALIDATING':
        return CreateGoalIllustration;
      case 'FINISHED':
        return CheckInSuccessIllustration;
      default:
        return CreateGoalIllustration;
    }
  };

  const getEmptyScreenDescriptionComponent = (): ReactNode => {
    if (forceShowUploadFileUI) {
      return (
        <UploadFileUI
          setRefetchUploadStatusToggle={setRefetchUploadStatusToggle}
          isInvalidFile={isInvalidFile}
          setInvalidFile={setInvalidFile}
          selectedFile={selectedFile}
          setSelectedFile={setSelectedFile}
          selectUploadFile={selectUploadFile}
          uploadingCSVFailed={uploadingCSVFailed}
          handleFileAttached={handleFileAttached}
          handleFileUploadClicked={handleFileUploadClicked}
          handleDownloadQuickGoalSampleFile={handleDownloadQuickGoalSampleFile}
          status={uploadStatus}
        />
      );
    }
    switch (uploadStatus?.status) {
      case 'IN_PROGRESS':
        return (
          <UploadingProgressUI
            totalRecords={uploadStatus?.totalRecords}
            recordsUpdated={uploadStatus?.recordsUpdated}
            handleDownloadQuickGoalSampleFile={handleDownloadQuickGoalSampleFile}
          />
        );
      case 'ERROR':
        return (
          <BulkUploadErrorUI
            setRefetchUploadStatusToggle={setRefetchUploadStatusToggle}
            isInvalidFile={isInvalidFile}
            setInvalidFile={setInvalidFile}
            selectedFile={selectedFile}
            setSelectedFile={setSelectedFile}
            selectUploadFile={selectUploadFile}
            csvWithErrorsUUID={uploadStatus?.uuid}
            uploadingCSVFailed={uploadingCSVFailed}
            handleFileAttached={handleFileAttached}
            handleFileUploadClicked={handleFileUploadClicked}
            handleDownloadQuickGoalSampleFile={handleDownloadQuickGoalSampleFile}
            status={uploadStatus}
          />
        );
      case 'VALIDATING':
        return (
          <UploadingProgressUI
            totalRecords={uploadStatus?.totalRecords}
            recordsUpdated={uploadStatus?.recordsUpdated}
            handleDownloadQuickGoalSampleFile={handleDownloadQuickGoalSampleFile}
          />
        );
      case 'FINISHED':
        return <BulkUploadSuccessUI setShowUploadFileUI={setForceShowUploadFileUI} />;
      default:
        return (
          <UploadFileUI
            setRefetchUploadStatusToggle={setRefetchUploadStatusToggle}
            isInvalidFile={isInvalidFile}
            setInvalidFile={setInvalidFile}
            selectedFile={selectedFile}
            setSelectedFile={setSelectedFile}
            selectUploadFile={selectUploadFile}
            uploadingCSVFailed={uploadingCSVFailed}
            handleFileAttached={handleFileAttached}
            handleFileUploadClicked={handleFileUploadClicked}
            handleDownloadQuickGoalSampleFile={handleDownloadQuickGoalSampleFile}
            status={uploadStatus}
          />
        );
    }
  };

  return (
    <Box
      p={3}
      background="#fff"
      height={checkIntentLandingPagesFeatureEnabled() ? 'calc(100vh - 162px)' : 'calc(100vh - 102px)'}
      display="flex"
      flexDirection="column"
      alignItems="center"
      overflow="auto"
      borderRadius="8px"
      boxShadow="0px 0px 2px 0px rgba(0, 0, 0, 0.24)"
    >
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        gap="8px"
        flex="1 0 0"
        alignSelf="stretch"
        borderRadius="8px"
        border="1px solid #F5F7F9 !important"
        background="#FDFDFD"
      >
        <Box width="400px" display="flex" flexDirection="column" flex="1" justifyContent="center" alignItems="center">
          <BulkUploadStatus
            // @ts-expect-error : wrong type
            getUploadStatus={(curStatus: {
              status: 'IN_PROGRESS' | 'ERROR' | 'VALIDATING' | 'FINISHED';
              recordsUpdated?: number;
              recordsFailed?: number;
            }) => {
              if (
                curStatus?.status !== uploadStatus?.status ||
                curStatus?.recordsUpdated !== uploadStatus?.recordsUpdated ||
                curStatus?.recordsFailed !== uploadStatus?.recordsFailed
              ) {
                // if last status was error or finished then start  the user journey from uploading file on first render
                if (isEmpty(uploadStatus) && (curStatus?.status === 'ERROR' || curStatus?.status === 'FINISHED')) {
                  setForceShowUploadFileUI(true);
                }

                // status changes in middle of user journey then show UI according to the new status
                else if (!isEmpty(uploadStatus) && forceShowUploadFileUI) {
                  setForceShowUploadFileUI(false);
                }
                setUploadStatus(curStatus);

                if (curStatus?.status === 'FINISHED') {
                  setSelectedFile(null);
                }
                if (curStatus?.status === 'ERROR') {
                  setUploadingCSVFailed(true);
                }
              }
            }}
            reload={refetchUploadStatusToggle}
            renderUI={() => null}
            uploadType="OKR_BULK_UPLOAD"
          />
          <EmptyScreen
            headerComp={getHeading}
            illustrationSrc={getEmptyScreenImage()}
            // eslint-disable-next-line react/no-unstable-nested-components
            descriptionComp={() => getEmptyScreenDescriptionComponent()}
          />
        </Box>{' '}
        {(isEmpty(uploadStatus) || uploadStatus?.status === 'IN_PROGRESS' || forceShowUploadFileUI) && (
          <Box display="flex" gap="8px" alignSelf="stretch" flexDirection="column" alignItems="flex-start">
            <Box display="flex">
              <Settings boxSize="24px" color="#999" />
              {/*  @ts-expect-error : union too complex */}
              <Flex justifyContent="center" alignItems="center">
                <ButtonToolkitVersion
                  sx={{
                    marginLeft: 'auto',
                    height: 'auto!important',
                    padding: '0px!important',
                    marginRight: '4px',
                    '.chakra-text': { fontSize: '14px', margin: '0px!important', lineHeight: '20px' },
                  }}
                  variant="tertiary"
                  onClick={() => {
                    handleViewGoalUploadGuide();
                    window.open(UPLOAD_GOAL_HELP_URL, '_blank', 'noopener,noreferrer');
                  }}
                >
                  Guide
                </ButtonToolkitVersion>
                <TextBodyMedium color="#666"> on how to create goals using upload on Mesh.</TextBodyMedium>
              </Flex>
            </Box>
            <Box display="flex">
              {/* change it to correct icon */}
              <Box display="flex" justifyContent="center" alignItems="center" padding="4px">
                <MeshGuide boxSize="16px" color="#999" />
              </Box>
              <Flex justifyContent="center" alignItems="center">
                <TextBodyMedium color="#666">Need to add more details for your goals? </TextBodyMedium>

                <ButtonToolkitVersion
                  sx={{
                    height: 'auto!important',
                    padding: '0px!important',
                    marginRight: '4px',
                    marginLeft: '4px',
                    '.chakra-text': { fontSize: '14px', margin: '0px!important', lineHeight: '20px' },
                  }}
                  variant="tertiary"
                  onClick={() => {
                    handleDownloadExtendedGoalSampleFile();
                    downloadBulkUploadOKRSampleFile({ sampleType: 'DETAILED' }).catch(() => {
                      toast({
                        title: 'An unknown error has occurred while downloading sample file',
                        status: 'error',
                      });
                    });
                  }}
                >
                  Download
                </ButtonToolkitVersion>
                <TextBodyMedium color="#666">the full goal sheet.</TextBodyMedium>
              </Flex>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export { BulkUploadScreen };
